import { MAX_ARRANGE_TILE_POSITION } from '@imagise/entities';
import { buildCopyName } from './animation-frames.utils';
import {
  EditorMergeImage,
  EditorMergeParams,
} from './editor-domain.models';

/**
 * Pure transforms over merge image lists. Unlike animation frames, no image
 * is special: the backend merges a free-form array, so any image can be
 * reordered or deleted — only the last remaining one is protected. The list
 * order is the draw order (later images render on top) and the grid cell
 * order. All functions return the input params unchanged on a no-op.
 */

export function appendMergeImages(
  params: EditorMergeParams,
  images: readonly Omit<EditorMergeImage, 'id'>[],
  createImageId: () => string,
): EditorMergeParams {
  return {
    ...params,
    images: [
      ...params.images,
      ...images.map(image => ({
        ...image,
        id: createImageId(),
      })),
    ],
  };
}

export function moveMergeImage(
  params: EditorMergeParams,
  id: string,
  position: number,
): EditorMergeParams {
  const images = [...params.images];
  const previousIndex = images.findIndex(image => image.id === id);

  if (previousIndex < 0) {
    return params;
  }

  const [image] = images.splice(previousIndex, 1);
  images.splice(Math.max(0, Math.min(position, images.length)), 0, image);

  return {
    ...params,
    images,
  };
}

export function duplicateMergeImage(
  params: EditorMergeParams,
  id: string,
  createImageId: () => string,
): EditorMergeParams {
  const imageIndex = params.images.findIndex(image => image.id === id);

  if (imageIndex < 0) {
    return params;
  }

  const image = params.images[imageIndex];
  const images = [...params.images];
  images.splice(imageIndex + 1, 0, {
    ...image,
    id: createImageId(),
    name: buildCopyName(image.name, params.images.map(existing => existing.name)),
  });

  return {
    ...params,
    images,
  };
}

export function deleteMergeImage(
  params: EditorMergeParams,
  id: string,
): EditorMergeParams {
  if (params.images.length <= 1) {
    return params;
  }

  const images = params.images.filter(image => image.id !== id);

  if (images.length === params.images.length) {
    return params;
  }

  return {
    ...params,
    images,
  };
}

export function setMergeImagePosition(
  params: EditorMergeParams,
  id: string,
  x: number,
  y: number,
): EditorMergeParams {
  return {
    ...params,
    images: params.images.map(image =>
      image.id === id
        ? {
            ...image,
            x: normalizeMergePosition(x),
            y: normalizeMergePosition(y),
          }
        : image,
    ),
  };
}

export function setMergeImagePositions(
  params: EditorMergeParams,
  positions: ReadonlyMap<string, { x: number; y: number }>,
): EditorMergeParams {
  return {
    ...params,
    images: params.images.map(image => {
      const position = positions.get(image.id);
      return position
        ? {
            ...image,
            x: normalizeMergePosition(position.x),
            y: normalizeMergePosition(position.y),
          }
        : image;
    }),
  };
}

function normalizeMergePosition(value: number): number {
  const rounded = Math.round(Number.isFinite(value) ? value : 0);
  return Math.min(MAX_ARRANGE_TILE_POSITION, Math.max(0, rounded));
}
